import { get } from 'lodash';
import _cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';
import { getAdjustedCabinetOrigin } from './cabinetLayout';
import { DIRECTION } from '../../../cabinetConstants';

/**
 * Handles duplicating a cabinet, spacer, or filler on the wall designer.
 * Finds the matching cabinet (top or bottom), clones it, and places the duplicate
 * next to the last cabinet on the specified anchor side (left or right).
 * Updates the design state and triggers the duplicate handler.
 *
 * @param {React.MouseEvent} event - The click event triggering the duplication.
 * @param {Object} params - Parameters for duplicating the cabinet.
 * @param {Array} params.cabinets - List of all cabinets.
 * @param {string} params.itemId - ID of the cabinet to duplicate.
 * @param {string} params.anchorSide - Side to anchor the duplicate ('left' or 'right').
 * @param {Function} params.addCabinetToDesign - Function to add cabinet to design.
 * @param {string} params.designId - ID of the design.
 * @param {Function} params.handleDuplicate - Function to handle duplication logic.
 * @param {string} params.host - Host URL for updates.
 * @param {Function} params.updateDesignProducts - Function to update design products.
 * @param {string} params.metadataBaseUrl - Base URL for metadata.
 * @param {Array} params.itemPrices - List of item prices.
 * @param {Function} params.trackAddDuplicateAnalytics - Function to send analytics on duplication.
 */
export const duplicateCabinet = (event, {
  cabinets,
  itemId,
  anchorSide,
  addCabinetToDesign,
  designId,
  handleDuplicate,
  host,
  updateDesignProducts,
  metadataBaseUrl,
  itemPrices,
  trackAddDuplicateAnalytics,
  id
}) => {
  event.preventDefault();
  event.stopPropagation();

  const matched = cabinets.find((cabinet) => cabinet.id === id);

  let newCabinet = _cloneDeep(matched);
  newCabinet.id = null; // Reset ID for the new cabinet
  const newCabinetDirection = anchorSide === DIRECTION.LEFT
    ? DIRECTION.RIGHT : DIRECTION.LEFT; // Always move away from the anchor side
  const newCabinetOrigin = getAdjustedCabinetOrigin({
    productType: matched.productType,
    origin: matched.Origin,
    cabinet: newCabinet,
    products: cabinets,
    direction: newCabinetDirection,
    editMode: false
  });

  newCabinet.Origin = newCabinetOrigin;
  newCabinet.id = uuidv4(); // Assign a new unique ID to the origin

  addCabinetToDesign(newCabinet, designId);
  handleDuplicate(newCabinet, cabinets, host, designId, updateDesignProducts, metadataBaseUrl);
  const itemPrice = itemPrices.find((item) => item.itemId === itemId);
  const basePrice = String(itemPrice?.price) || '0';
  trackAddDuplicateAnalytics(basePrice, itemId, 1);
};
